import React, { MutableRefObject, useCallback, useMemo, useRef, useState } from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import TurnRightIcon from "@mui/icons-material/TurnRight";
import { Avatar, Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { RichTextEditorComponent } from "@syncfusion/ej2-react-richtexteditor";
import aiAvatar from "assets/images/ai_avatar.png";
import { DocumentAnswerState } from "constants/documents";
import { useRoleGuardHook } from "hooks";
import { useSelectedQuestionSelect, useUserIdSelector } from "store";
import { UserLight } from "store/types";
import { DocumentAnswer as DocumentAnswerType, DocumentQuestion } from "store/types";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";
import { AnswerContextMenu } from "../Menus/AnswerContextMenu";
import { DocumentTextAnswer } from "./DocumentTextAnswer";

interface DocumentAnswerProp {
  documentId: number;
  question: DocumentQuestion;
  answer: DocumentAnswerType;
}

interface DocumentAnswerContentProps {
  onChange: (value: string) => void;
  rteRef: MutableRefObject<RichTextEditorComponent | null>;
  onRtlSwitch?: (value: boolean) => void;
}

const DocumentAnswerContent = (props: DocumentAnswerProp & DocumentAnswerContentProps) => {
  return (
    <DocumentTextAnswer
      onRtlSwitch={props.onRtlSwitch}
      rteRef={props.rteRef}
      onChange={props.onChange}
      readonly={!!props.answer.published_at && props.answer.state === DocumentAnswerState.Published}
      answer={props.answer}
      cssClass={"rich-text-editor-documents-questionnaire"}
    />
  );
};

const aiAvatarAnswer: Omit<UserLight, "id"> = {
  profile_photo_url: aiAvatar,
  full_name: "AI Answer"
};

export const DocumentAnswer: React.FC<DocumentAnswerProp> = (props) => {
  const { documentId, answer, question } = props;
  const user = answer.user || aiAvatarAnswer;
  const userId = useUserIdSelector();
  const selectedQuestion = useSelectedQuestionSelect();
  const isSelectedQuestion = useMemo(() => selectedQuestion?.id === question.id, [selectedQuestion, question]);
  const { getAnswers, updateAnswer, deleteAnswer, createAnswer } = useContentContext();
  const subAnswers = getAnswers({ parent_answer_id: answer.id });

  const handlePublish = useCallback(
    (value?: string) => {
      updateAnswer({
        documentId,
        id: answer.id,
        is_published: true,
        ...(value && { answer: { text: value } })
      });
    },
    [answer, updateAnswer]
  );

  const handleChange = useCallback(
    (value: string) => {
      updateAnswer({ documentId, id: answer.id, answer: { text: value }, disableSetState: true });
    },
    [answer, documentId, updateAnswer]
  );

  const onRtlSwitch = useCallback(
    (value: boolean) => {
      updateAnswer({ documentId, id: answer.id, is_rtl: value });
    },
    [documentId, answer, updateAnswer]
  );

  const handleEdit = () => {
    updateAnswer({ documentId, id: props.answer.id, is_published: false });
    setMenuOpen(false);
  };

  const handleEditOwnAnswer = useMemo(() => {
    if (!userId) return;
    if (props.answer.user_id !== userId) return;
    return handleEdit;
  }, [userId, props, handleEdit]);

  const handleDelete = () => {
    deleteAnswer(answer.id);
    setMenuOpen(false);
  };

  const handleDeleteRole = useRoleGuardHook(handleDelete, [RoleName.Admin, RoleName.Analyst]);

  const isAnswerOwner = answer.user_id === userId ? handleDelete : undefined;

  // const handleCancel = () => {
  //   deleteAnswer(answer.id);
  // };

  const handleCancel = () => {
    if (answer.state === DocumentAnswerState.Draft && answer.published_at) {
      updateAnswer({ documentId, id: props.answer.id, is_published: true });
    } else {
      deleteAnswer(answer.id);
    }
  };

  const handleAddAnswer = () => {
    // TODO: answers might not be text answers
    const unPublishedAnswer = subAnswers.find((item) => !item.published_at);
    if (unPublishedAnswer) return; // Don't let create one more reply if at least 1 unpublished answer
    createAnswer({
      documentId,
      question_id: question.id,
      parent_answer_id: answer.id,
      answer: { text: "" }
    });
  };

  const svgRef = useRef<SVGSVGElement>(null);
  const rteRef = useRef<RichTextEditorComponent>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const readonly = !!props.answer.published_at || !answer.user;
  const isReplyEnabled = useRoleGuardHook(true, [RoleName.Admin, RoleName.Analyst, RoleName.Guest]);

  return (
    <Stack
      sx={{
        ml: "20px",
        p: "8px",
        borderRadius: "5px",
        bgcolor: "white",
        ":hover": { ".answer-menus": { opacity: 1 } }
      }}
    >
      {readonly && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" dir="auto">
          <Stack direction="row" alignItems="center" gap="10px">
            <Avatar sx={{ width: "28px", height: "28px" }} alt={user.full_name} src={user.profile_photo_url} />
            <Typography sx={{ fontWeight: 500, color: "#0a3142", fontSize: "14px" }}>{user.full_name}</Typography>
            <Typography sx={{ color: "#5d7782", fontSize: "14px" }}>
              {Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "short" }).format(
                new Date(answer.published_at as string)
              )}
            </Typography>
          </Stack>
          {answer.user && ( // Don't show this block if answer from AI
            <Stack
              className="answer-menus"
              sx={{ gap: "5px", opacity: 0, transition: "opacity 0.2s linear" }}
              direction="row"
            >
              {(handleDeleteRole || handleEditOwnAnswer) && (
                <IconButton sx={{ padding: "4px" }} onClick={() => setMenuOpen(true)}>
                  <MoreHorizIcon sx={{ fontSize: 16 }} ref={svgRef} />
                </IconButton>
              )}
              {isReplyEnabled && (
                <IconButton sx={{ padding: "4px" }} onClick={handleAddAnswer}>
                  <TurnRightIcon sx={{ fontSize: 16, transform: "rotate(180deg)" }} />
                </IconButton>
              )}
            </Stack>
          )}
          <AnswerContextMenu
            anchorEl={svgRef.current}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            onDelete={handleDeleteRole || isAnswerOwner}
            onEdit={handleEditOwnAnswer}
          />
        </Stack>
      )}
      <Box
        dir="auto"
        sx={{
          // rte styles
          ".e-richtexteditor.e-rte-tb-expand": {
            border: "none"
          },
          ".e-toolbar-item:first-child": {
            ml: "-10px !important"
          },
          ".e-toolbar-items": {},
          ".e-content": {
            pt: "10px !important",
            ...(!readonly && { borderBottom: "1px solid #5d7782" })
          },
          "& LI, & P": {
            color: "#597380",
            lineHeight: "24px",
            fontFamily: "Roboto, sans-serif",
            fontSize: "16px"
          },
          ".rte-placeholder": {
            top: "10px !important",
            padding: "0 !important"
          }
        }}
      >
        <DocumentAnswerContent onRtlSwitch={onRtlSwitch} {...props} rteRef={rteRef} onChange={handleChange} />
      </Box>
      {answer.state === DocumentAnswerState.Draft && isSelectedQuestion && (
        <Stack direction="row" gap="10px" pt="10px">
          <Button
            size="sm"
            sx={{ fontSize: 12, minHeight: "18px", minWidth: "unset" }}
            onClick={() => rteRef.current?.value && handlePublish(rteRef.current?.value)}
            color="primary"
            variant="contained"
          >
            Publish
          </Button>
          <Button sx={{ fontSize: 12, minHeight: "18px", minWidth: "unset" }} onClick={handleCancel} variant="text">
            Cancel
          </Button>
        </Stack>
      )}
      {subAnswers.map((subAnswer) => (
        <DocumentAnswer key={subAnswer.id} documentId={documentId} question={props.question} answer={subAnswer} />
      ))}
    </Stack>
  );
};
